import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config/apiConfig';
import { normalizeTitlesApiLang } from '../utils/battleTitleLocalization';
import {
  DEV_BATTLE_SETUP_HAND_COUNT,
  DEV_BATTLE_SETUP_SLOT_COUNT,
  DEV_BATTLE_SETUP_SLOT_LABELS,
  buildDevBattleSetupSnapshot,
  buildDevSetupBattleAction,
} from './devBattleSetupSlots';
import './DevBattleSetupDialog.css';

function cloneFormState(snapshot) {
  return {
    slots: [...snapshot.slots],
    handUnitIds: [...snapshot.handUnitIds],
    playerIntel: snapshot.playerIntel,
    playerMaxIntel: snapshot.playerMaxIntel,
    playerHealth: snapshot.playerHealth,
    opponentIntel: snapshot.opponentIntel,
    opponentMaxIntel: snapshot.opponentMaxIntel,
    opponentHealth: snapshot.opponentHealth,
    opponentName: snapshot.opponentName,
    opponentTitleId: snapshot.opponentTitleId,
    showAutowin: snapshot.showAutowin,
    showDevConsole: snapshot.showDevConsole,
  };
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object|null} props.gameState
 * @param {string|number|null} props.userId
 * @param {string|null} props.opponentUsername
 * @param {{ showAutowin?: boolean, showDevConsole?: boolean, opponentName?: string, opponentTitleId?: number|null }|null} props.devBattleSetupUi
 * @param {(payload: { serverAction: object, uiPatch: object }) => void|Promise<void>} props.onApply
 */
export default function DevBattleSetupDialog({
  open,
  onClose,
  gameState,
  userId,
  opponentUsername,
  devBattleSetupUi,
  onApply,
}) {
  const [form, setForm] = useState(() =>
    cloneFormState(
      buildDevBattleSetupSnapshot({
        gameState,
        userId,
        opponentUsername,
        devBattleSetupUi,
      })
    )
  );
  const [titles, setTitles] = useState([]);
  const [titlesLoading, setTitlesLoading] = useState(false);
  const [titlesError, setTitlesError] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyError, setApplyError] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setApplyError(null);
    setForm(
      cloneFormState(
        buildDevBattleSetupSnapshot({
          gameState,
          userId,
          opponentUsername,
          devBattleSetupUi,
        })
      )
    );
  }, [open, gameState, userId, opponentUsername, devBattleSetupUi]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setTitlesError('Login token required to load titles');
      setTitles([]);
      return;
    }

    let cancelled = false;
    setTitlesLoading(true);
    setTitlesError(null);

    const fromStorage =
      typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
    const apiLang = normalizeTitlesApiLang(fromStorage || 'en-us');

    fetch(`${API_URL}/profile/titles?lang=${encodeURIComponent(apiLang)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept-Language': apiLang,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`profile/titles failed (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data.titles)) {
          throw new Error('profile/titles response missing titles array');
        }
        setTitles(data.titles);
      })
      .catch((err) => {
        if (cancelled) return;
        setTitlesError(err instanceof Error ? err.message : String(err));
        setTitles([]);
      })
      .finally(() => {
        if (!cancelled) {
          setTitlesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const updateSlot = useCallback((index, value) => {
    setForm((prev) => {
      const slots = [...prev.slots];
      slots[index] = value;
      return { ...prev, slots };
    });
  }, []);

  const updateHand = useCallback((index, value) => {
    setForm((prev) => {
      const handUnitIds = [...prev.handUnitIds];
      handUnitIds[index] = value;
      return { ...prev, handUnitIds };
    });
  }, []);

  const handleApply = async () => {
    setApplyError(null);
    setApplyBusy(true);
    try {
      const serverAction = buildDevSetupBattleAction(form);
      const selectedTitle =
        form.opponentTitleId === '' || form.opponentTitleId == null
          ? null
          : titles.find((row) => String(row.id) === String(form.opponentTitleId));
      if (form.opponentTitleId !== '' && form.opponentTitleId != null && !selectedTitle) {
        throw new Error('Selected opponent title was not found in the titles catalog');
      }

      const uiPatch = {
        opponentName: form.opponentName,
        opponentTitleId:
          form.opponentTitleId === '' || form.opponentTitleId == null
            ? null
            : Number(form.opponentTitleId),
        opponentTitleInjected: selectedTitle?.nameAsInjectedForBattle ?? null,
        opponentTitleColor: selectedTitle?.color ?? 'white',
        showAutowin: form.showAutowin === true,
        showDevConsole: form.showDevConsole === true,
      };

      await onApply({ serverAction, uiPatch });
      onClose();
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : String(err));
    } finally {
      setApplyBusy(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="dev-battle-setup-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dev-battle-setup-title"
      data-battle-keyboard-shortcuts-ignore
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="dev-battle-setup-dialog">
        <h2 id="dev-battle-setup-title">Dev battle setup</h2>
        <p className="dev-battle-setup-subtitle">
          Configure battlefield slots, hand, stats, opponent display, and dev UI visibility for
          screenshots and scenario testing.
        </p>

        {applyError ? (
          <p className="dev-battle-setup-error" role="alert">
            {applyError}
          </p>
        ) : null}
        {titlesError ? (
          <p className="dev-battle-setup-error" role="alert">
            Titles: {titlesError}
          </p>
        ) : null}

        <section className="dev-battle-setup-section">
          <h3>Battlefield ({DEV_BATTLE_SETUP_SLOT_COUNT} slots)</h3>
          <div className="dev-battle-setup-slots-grid">
            {Array.from({ length: DEV_BATTLE_SETUP_SLOT_COUNT }, (_, index) => (
              <div key={`slot-${index}`} className="dev-battle-setup-slot">
                <label htmlFor={`dev-setup-slot-${index}`}>{DEV_BATTLE_SETUP_SLOT_LABELS[index]}</label>
                <input
                  id={`dev-setup-slot-${index}`}
                  type="text"
                  inputMode="numeric"
                  placeholder="ID"
                  value={form.slots[index] ?? ''}
                  onChange={(e) => updateSlot(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="dev-battle-setup-section">
          <h3>Intel &amp; health</h3>
          <div className="dev-battle-setup-stats-grid">
            <div className="dev-battle-setup-side-stats">
              <h4>Player</h4>
              <div className="dev-battle-setup-stat-row">
                <div className="dev-battle-setup-field">
                  <label htmlFor="dev-setup-player-intel">Intel</label>
                  <input
                    id="dev-setup-player-intel"
                    type="number"
                    min={0}
                    value={form.playerIntel}
                    onChange={(e) => setForm((prev) => ({ ...prev, playerIntel: e.target.value }))}
                  />
                </div>
                <div className="dev-battle-setup-field">
                  <label htmlFor="dev-setup-player-max-intel">Max intel</label>
                  <input
                    id="dev-setup-player-max-intel"
                    type="number"
                    min={0}
                    value={form.playerMaxIntel}
                    onChange={(e) => setForm((prev) => ({ ...prev, playerMaxIntel: e.target.value }))}
                  />
                </div>
                <div className="dev-battle-setup-field">
                  <label htmlFor="dev-setup-player-health">Health</label>
                  <input
                    id="dev-setup-player-health"
                    type="number"
                    min={0}
                    value={form.playerHealth}
                    onChange={(e) => setForm((prev) => ({ ...prev, playerHealth: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="dev-battle-setup-side-stats">
              <h4>Opponent</h4>
              <div className="dev-battle-setup-stat-row">
                <div className="dev-battle-setup-field">
                  <label htmlFor="dev-setup-opponent-intel">Intel</label>
                  <input
                    id="dev-setup-opponent-intel"
                    type="number"
                    min={0}
                    value={form.opponentIntel}
                    onChange={(e) => setForm((prev) => ({ ...prev, opponentIntel: e.target.value }))}
                  />
                </div>
                <div className="dev-battle-setup-field">
                  <label htmlFor="dev-setup-opponent-max-intel">Max intel</label>
                  <input
                    id="dev-setup-opponent-max-intel"
                    type="number"
                    min={0}
                    value={form.opponentMaxIntel}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, opponentMaxIntel: e.target.value }))
                    }
                  />
                </div>
                <div className="dev-battle-setup-field">
                  <label htmlFor="dev-setup-opponent-health">Health</label>
                  <input
                    id="dev-setup-opponent-health"
                    type="number"
                    min={0}
                    value={form.opponentHealth}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, opponentHealth: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="dev-battle-setup-section">
          <h3>Opponent display</h3>
          <div className="dev-battle-setup-opponent-row">
            <div className="dev-battle-setup-field">
              <label htmlFor="dev-setup-opponent-name">Name</label>
              <input
                id="dev-setup-opponent-name"
                type="text"
                value={form.opponentName}
                onChange={(e) => setForm((prev) => ({ ...prev, opponentName: e.target.value }))}
              />
            </div>
            <div className="dev-battle-setup-field">
              <label htmlFor="dev-setup-opponent-title">Title</label>
              <select
                id="dev-setup-opponent-title"
                value={form.opponentTitleId}
                disabled={titlesLoading}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, opponentTitleId: e.target.value }))
                }
              >
                <option value="">(none)</option>
                {titles.map((row) => (
                  <option key={row.id} value={String(row.id)}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="dev-battle-setup-section">
          <h3>Player hand (up to {DEV_BATTLE_SETUP_HAND_COUNT})</h3>
          <div className="dev-battle-setup-hand-grid">
            {Array.from({ length: DEV_BATTLE_SETUP_HAND_COUNT }, (_, index) => (
              <div key={`hand-${index}`} className="dev-battle-setup-field">
                <label htmlFor={`dev-setup-hand-${index}`}>Card {index + 1}</label>
                <input
                  id={`dev-setup-hand-${index}`}
                  type="text"
                  inputMode="numeric"
                  placeholder="ID"
                  value={form.handUnitIds[index] ?? ''}
                  onChange={(e) => updateHand(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="dev-battle-setup-section">
          <h3>Dev UI visibility</h3>
          <div className="dev-battle-setup-toggles">
            <label>
              <input
                type="checkbox"
                checked={form.showAutowin === true}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, showAutowin: e.target.checked }))
                }
              />
              Show Autowin button
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.showDevConsole === true}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, showDevConsole: e.target.checked }))
                }
              />
              Show dev console
            </label>
          </div>
        </section>

        <div className="dev-battle-setup-actions">
          <button type="button" className="dev-battle-setup-cancel" disabled={applyBusy} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dev-battle-setup-apply" disabled={applyBusy} onClick={handleApply}>
            {applyBusy ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}
